import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { signIn, signOut, getCurrentUser, fetchAuthSession } from "@aws-amplify/auth";
import { GET_USER_DATA } from "../../apollo/queries/ClientProfile";
import { GET_MANUFACTURER_BY_SUBDOMAIN } from "../../apollo/queries/getManufacturerBySubDomain";
import { ENSURE_MY_USER_RECORD } from "../../apollo/mutations/ensureMyUserRecord";
import client from "../../apollo/client";
import { getManufacturerRequestedSubdomain } from "../../utils/subdomainFromHostname";

interface User {
  email?: string;
  firstName?: string;
  lastName?: string;
  [key: string]: any;
}

export enum PasswordField {
  Password = "password",
  RegisterPassword = "registerPassword",
  NewPassword = "newPassword",
  RepeatPassword = "repeatPassword",
  RepeatNewPassword = "repeatNewPassword",
}

export type PasswordVisibility = {
  [PasswordField.Password]: boolean;
  [PasswordField.RegisterPassword]: boolean;
  [PasswordField.NewPassword]: boolean;
  [PasswordField.RepeatPassword]: boolean;
  [PasswordField.RepeatNewPassword]: boolean;
};

const defaultPasswordVisibility: PasswordVisibility = {
  password: false,
  registerPassword: false,
  newPassword: false,
  repeatPassword: false,
  repeatNewPassword: false,
};

interface AuthStore {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  isError: boolean;
  isAction: any;
  isOpen: boolean;
  show: boolean;
  toggle: boolean;
  domain: string | null; 
  brandName: string | null;
  domainResolved: boolean;
  initDomainFromManufacturer: () => Promise<string | null>;

  passwordVisibility: PasswordVisibility;
  setPasswordVisibility: (patch: Partial<PasswordVisibility>) => void;
  setPasswordField: (field: PasswordField, value: boolean) => void;
  togglePasswordField: (field: PasswordField) => void;

  togglePassword: () => void;
  toggleRegisterPassword: () => void;
  toggleNewPassword: () => void;
  toggleRepeatPassword: () => void;
  toggleRepeatNewPassword: () => void;

  login: (credentials: {
    email: string;
    password: string;
  }) => Promise<{ success: boolean; user: User | null }>;
  logout: () => Promise<void>;
  clearError: () => void;
  reset: () => void;
  setShow: (show: boolean) => void;
  setIsOpen: (isOpen: boolean) => void;
  setIsAction: (action: any) => void;
  setToggle: (toggle: boolean) => void;
  fetchFullUserProfile: () => Promise<void>;
  setError: (error: boolean) => void;

  getFullName: () => string;
}

export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      (set, get, _api) => {
        const initialState: AuthStore = {
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        isError: false,
        isAction: null,
        isOpen: false,
        show: false,
        toggle: false,
        domain: null,
        brandName: null,
        domainResolved: false,
        initDomainFromManufacturer: async () => {
          try {
            const hostname = window.location.hostname.toLowerCase();
            const initialSub = getManufacturerRequestedSubdomain(hostname);

            if (!initialSub) {
              set({ domain: null, domainResolved: true });
              return null;
            }

            const { data } = await client.query({
              query: GET_MANUFACTURER_BY_SUBDOMAIN,
              variables: { subDomain: initialSub },
              fetchPolicy: "network-only",
            });

            const apiSub: string | null = data?.getManufacturerBySubDomain?.subDomain || null;
            const apiBrandName: string | null = data?.getManufacturerBySubDomain?.brand || null;
            set({ domain: apiSub, brandName: apiBrandName, domainResolved: true });
            return apiSub;
          } catch (_err) {
            set({ domain: null, brandName: null, domainResolved: true });
            return null;
          }
        },
        
        passwordVisibility: { ...defaultPasswordVisibility },

        setPasswordVisibility: (patch) =>
          set((s) => ({
            passwordVisibility: { ...s.passwordVisibility, ...patch },
          })),

        setPasswordField: (field, value) =>
          set((s) => ({
            passwordVisibility: { ...s.passwordVisibility, [field]: value },
          })),

        togglePasswordField: (field) =>
          set((s) => ({
            passwordVisibility: {
              ...s.passwordVisibility,
              [field]: !s.passwordVisibility[field],
            },
          })),

        togglePassword: () =>
          set((s) => ({
            passwordVisibility: {
              ...s.passwordVisibility,
              password: !s.passwordVisibility.password,
            },
          })),
        toggleRegisterPassword: () =>
          set((s) => ({
            passwordVisibility: {
              ...s.passwordVisibility,
              registerPassword: !s.passwordVisibility.registerPassword,
            },
          })),
        toggleNewPassword: () =>
          set((s) => ({
            passwordVisibility: {
              ...s.passwordVisibility,
              newPassword: !s.passwordVisibility.newPassword,
            },
          })),
        toggleRepeatPassword: () =>
          set((s) => ({
            passwordVisibility: {
              ...s.passwordVisibility,
              repeatPassword: !s.passwordVisibility.repeatPassword,
            },
          })),
        toggleRepeatNewPassword: () =>
          set((s) => ({
            passwordVisibility: {
              ...s.passwordVisibility,
              repeatNewPassword: !s.passwordVisibility.repeatNewPassword,
            },
          })),

        login: async (credentials) => {
          set({ isLoading: true, error: null });
          try {
            try {
              await getCurrentUser();
              await signOut();
            } catch {
              /* no active Cognito session */
            }

            const response = await signIn({
              username: credentials.email,
              password: credentials.password,
            });
            // console.log("response",response)
            // if (!response.isSignedIn) throw new Error("Login failed");

            if (response.isSignedIn) {
              await get().fetchFullUserProfile();
              const userAfter = get().user;
              if (!userAfter) {
                set((s) => ({
                  isLoading: false,
                  error:
                    s.error ??
                    "Could not load your profile. If this persists, verify Cognito User Pool and Identity Pool settings match your environment.",
                }));
                return { success: false, user: null };
              }
              set({ isLoading: false });
              return { success: true, user: userAfter };
            }

            if (response.nextStep?.signInStep === "CONFIRM_SIGN_UP") {
              return { success: false, user: get().user, nextStep: response.nextStep };
            }


            throw new Error("Login failed");
          } catch (error) {
            set({
              error: error.message,
              isLoading: false,
            });
            // console.log("errors",error)
            throw error;
          }
        },

        fetchFullUserProfile: async () => {
          try {
            try {
              await getCurrentUser();
            } catch {
              return;
            }
            const { tokens } = await fetchAuthSession();
            if (!tokens?.idToken) {
              return;
            }

            set({ isLoading: true });
            let { data } = await client.query({
              query: GET_USER_DATA,
              fetchPolicy: "network-only",
            });
            let fullUser = data?.user ?? null;
            if (!fullUser) {
              try {
                await client.mutate({ mutation: ENSURE_MY_USER_RECORD });
                ({ data } = await client.query({
                  query: GET_USER_DATA,
                  fetchPolicy: "network-only",
                }));
                fullUser = data?.user ?? null;
              } catch (ensureErr: unknown) {
                console.warn(
                  "ensureMyUserRecord failed",
                  ensureErr instanceof Error ? ensureErr.message : ensureErr
                );
              }
            }
            if (!fullUser) {
              set({
                error:
                  "We could not load your profile from our records. This often happens if sign-up did not finish saving (for example, a server error during confirmation). Try creating the account again or contact support.",
                isLoading: false,
              });
              return;
            }

            set({
              user: fullUser,
              isAuthenticated: true,
              isLoading: false,
            });
          } catch (err: any) {
            set({ error: err.message, isLoading: false });
            console.error("Failed to fetch full profile", err);
          }
        },

        logout: async () => {
          try {
            await signOut();
            set({
              user: null,
              isAuthenticated: false,
              error: null,
              isError: false,
              passwordVisibility: { ...defaultPasswordVisibility },
            });
          } catch (error: any) {
            set({ error: error.message });
          }
        },

        clearError: () => set({ error: null }),

        reset: () => {
          set({
            user: null,
            isAuthenticated: false,
            isError: false,
            passwordVisibility: { ...defaultPasswordVisibility },
          });
        },

        setShow: (show: boolean) => set({ show }),
        setIsOpen: (isOpen: boolean) => set({ isOpen }),
        setIsAction: (action: any) => set({ isAction: action }),
        setToggle: (toggle: boolean) => set({ toggle }),
        setError: (error: boolean) => set({ isError: error }),

        getFullName: () => {
          const { user } = get();
          return user
            ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
            : "";
        },

        };

        setTimeout(() => {
          const s = get();
          if (s.initDomainFromManufacturer) s.initDomainFromManufacturer();
        }, 0);

        return initialState;
      },
      {
        name: "user",
        partialize: (state) => ({
          user: state.user,
          isAuthenticated: state.isAuthenticated,
          domain: state.domain,
          brandName: state.brandName,
        }),
      }
    )
  )
);

export const useLoggedIn = () => {
  const {
    user,
    isError,
    isAction,
    isOpen,
    show,
    toggle,
    isLoading: loading,
    setShow,
    setIsOpen,
    setIsAction,
    setToggle,
    reset,
    domain,
    brandName,
  } = useAuthStore();

  return {
    user,
    isError,
    isAction,
    isOpen,
    show,
    toggle,
    loading,
    setShow,
    setIsOpen,
    setIsAction,
    setToggle,
    reset,
    domain, 
    brandName,
  };
};
